import * as React from 'react';
import { Edit3, Trash2, X } from 'lucide-react';
import avatarPlaceholder from 'assets/icons/Avatar.svg';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import type { LeadershipProfile } from 'features/DigitalMarketing/pages/EditWebsiteContent/LeadershipProfiles/LeadershipProfileList';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMText,
  DMBr,
} from '../../../components/Primitives';

type Props = {
  profile: LeadershipProfile | null;
  onClose: () => void;
  onDeleteSuccess?: (profile: LeadershipProfile) => void | Promise<void>;
  onEdit?: (profile: LeadershipProfile) => void;
};

export function LeadershipProfileModal({
  profile,
  onClose,
  onDeleteSuccess,
  onEdit,
}: Props) {
  const [deleteStep, setDeleteStep] = React.useState<'idle' | 'confirm'>(
    'idle'
  );
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!profile) {
      setDeleteStep('idle');
      setIsDeleting(false);
    }
  }, [profile]);

  if (!profile) {
    return null;
  }

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      await onDeleteSuccess?.(profile);
      setDeleteStep('idle');
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DMDiv
        className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/20 px-[2vw]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="leadership-profile-title"
        onClick={onClose}
      >
        <DMDiv
          className="w-full max-w-[780px] rounded-[4px] bg-white px-[40px] pb-[38px] pt-[34px] shadow-[0_18px_45px_rgba(15,23,42,0.18)]"
          onClick={event => event.stopPropagation()}
        >
          <DMDiv className="mb-[28px] flex items-center justify-between">
            <DMText
              as="h2"
              id="leadership-profile-title"
              className="text-[22px] font-bold leading-none text-black"
            >
              Profile
            </DMText>
            <DMButton
              type="button"
              onClick={onClose}
              className="flex h-[28px] w-[28px] items-center justify-center text-black"
              aria-label="Close profile"
            >
              <X className="h-[25px] w-[25px]" strokeWidth={2.2} />
            </DMButton>
          </DMDiv>

          <DMDiv className="mb-[24px] flex min-h-[148px] items-center gap-[18px] rounded-[18px] border border-gray-200 bg-gray-50 px-[20px] py-[18px]">
            <DMImage
              src={profile.image || avatarPlaceholder}
              alt={profile.facultyName}
              onError={event => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = avatarPlaceholder;
              }}
              className="h-[106px] w-[106px] shrink-0 rounded-full object-cover"
            />
            <DMDiv className="min-w-0">
              <DMDiv className="mb-[10px] flex flex-wrap items-center gap-[12px]">
                <DMText
                  as="h3"
                  className="text-[24px] font-bold leading-none text-black"
                >
                  {profile.facultyName}
                </DMText>
                <DMText
                  as="span"
                  className="rounded-full bg-[#ece8ff] px-[10px] py-[7px] text-[14px] font-semibold leading-none text-primary-main"
                >
                  {profile.qualification}
                </DMText>
                <DMText
                  as="span"
                  className="text-[17px] font-semibold leading-none text-gray-600"
                >
                  {profile.role}
                </DMText>
              </DMDiv>
              <DMText className="text-[16px] font-medium leading-[1.55] text-gray-500">
                Experience :{' '}
                <DMText as="span" className="font-bold">
                  {profile.experience}
                </DMText>
                <DMBr />
                {profile.email}
                <DMBr />
                {[profile.countryCode, profile.mobile]
                  .filter(Boolean)
                  .join(' ')}
              </DMText>
            </DMDiv>
          </DMDiv>

          {profile.about ? (
            <DMDiv className="mb-[42px] rounded-[14px] border border-gray-200 bg-white px-[18px] py-[16px]">
              <DMText
                as="h4"
                className="mb-[8px] text-[15px] font-bold leading-none text-gray-900"
              >
                Description
              </DMText>
              <DMText className="max-h-[160px] overflow-auto text-[15px] font-medium leading-[1.55] text-gray-600">
                {profile.about}
              </DMText>
            </DMDiv>
          ) : null}

          <DMDiv className="grid grid-cols-2 gap-[24px]">
            <DMButton
              type="button"
              onClick={() => setDeleteStep('confirm')}
              className="flex h-[52px] items-center justify-center gap-[10px] rounded-full border border-red-500 bg-white text-[16px] font-semibold text-red-500"
            >
              <Trash2 className="h-[20px] w-[20px]" strokeWidth={2} />
              <DMText as="span">Delete Profile</DMText>
            </DMButton>
            <DMButton
              type="button"
              onClick={() => onEdit?.(profile)}
              className="flex h-[52px] items-center justify-center gap-[10px] rounded-full bg-primary-main text-[16px] font-semibold text-white"
            >
              <Edit3 className="h-[20px] w-[20px]" strokeWidth={2} />
              <DMText as="span">Edit Profile</DMText>
            </DMButton>
          </DMDiv>
        </DMDiv>
      </DMDiv>

      <DigitalMarketingDeleteModal
        isOpen={deleteStep === 'confirm'}
        title="Are you sure you want to delete this profile?"
        isDeleting={isDeleting}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteStep('idle');
          }
        }}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
